import { removeAdditionalExamModule } from '@/store/registerModule'
import Vue from 'vue'
import { cloneDeep } from 'lodash-es'
import {
  summaryTypes,
  addAdditionalExamsFromForms,
  addAdditionalExamsToForm,
  populateMedicalRecords
} from '@/utils/attendanceHelper'

export default {
  setFormModules(state, modules) {
    state.attendanceFormModules = { ...modules }
  },
  setSelectedForm(state, form) {
    state.selectedForm = form
  },
  setCustomFormEdit(state, value) {
    state.customFormEdit = value
  },
  setLoading(state, loading) {
    state.loading = loading
  },
  updateMedicalRecord(state, medicalRecord) {
    // Consulta já limpa do store (navegação/finalização) enquanto um save
    // assíncrono do "Importar dados"/refração ainda estava em voo: ignora o
    // callback obsoleto em vez de quebrar em `attendance.medicalRecords`.
    if (!state.attendance) return
    const attendance = state.attendance
    const index = attendance.medicalRecords.findIndex(
      record => record.type === medicalRecord.type
    )
    if (index >= 0) {
      attendance.medicalRecords[index].value = medicalRecord.value
    } else {
      attendance.medicalRecords.push(medicalRecord)
    }
    state.attendance = {
      ...attendance,
      medicalRecords: [...attendance.medicalRecords]
    }
  },
  clearAttendance(state) {
    if (state) {
      state.canEdit = true
      state.attendance = null
      // Ao sair da consulta, zera o estado de salvamento para não vazar para
      // a próxima (sinal vermelho preso / "Edição bloqueada" indevido).
      state.savingMedicalRecord = false
      state.savingMedicalRecordError = null
      state.savedMedicalRecordTypes = new Set()
      state.persistedMedicalRecordValues = {}
      if (state.form && Object.keys(state.form).length) {
        Object.keys(state.form).map(key => {
          if (key.includes('exame-personalizado')) {
            removeAdditionalExamModule(key)
            return
          }
          if (state.form[key] && state.form[key].id) {
            try {
              state.form[key].summary !== undefined &&
                delete state.form[key].summary
              state.form[key].completed = false
              state.form[key].multiple
                ? (state.form[key].value = [])
                : Object.keys(state.form[key].fields).map(field => {
                    const value = state.form[key].fields[field]
                    state.form[key].fields[field] = Array.isArray(value)
                      ? []
                      : value === false || value === true
                      ? value
                      : null
                  })
            } catch (e) {
              console.log(`erro no modulo ${key} - ${e}`)
            }
          }
        })
      }
    }
  },
  setAttendance(state, payload) {
    state.canEdit =
      ['OPENED', 'PAUSED'].includes(payload.status) ||
      (payload.type === 'EXAM' && payload.status === 'PENDING') ||
      (payload.status === 'CLOSED' && payload.type === 'MEDICAL_REPORT')
    state.attendance = payload
    // Semeia o snapshot persistido com os valores vindos do servidor (clone
    // profundo para não aliasar com attendance.medicalRecords, que é mutado
    // de forma otimista). A partir daqui a idempotência compara contra isto.
    const persisted = {}
    if (Array.isArray(payload?.medicalRecords)) {
      payload.medicalRecords.forEach(mr => {
        if (mr && mr.type !== undefined && mr.type !== null) {
          persisted[mr.type] = cloneDeep(mr.value)
        }
      })
    }
    state.persistedMedicalRecordValues = persisted
  },
  // Registra o valor que ACABOU de ser persistido com sucesso (pós-PUT).
  // Só isto move a fonte de verdade da idempotência para frente.
  setPersistedMedicalRecordValue(state, { type, value }) {
    if (type === undefined || type === null) return
    state.persistedMedicalRecordValues = {
      ...state.persistedMedicalRecordValues,
      [type]: cloneDeep(value)
    }
  },
  setPreConsultation(state, payload) {
    state.preConsultation = payload
  },
  setCidConsultation(state, payload) {
    state.cidConsultation = payload
  },
  setBvConsultation(state, payload) {
    state.bvConsultation = payload
  },
  setExam(state, payload) {
    state.exam = payload
  },
  closeAttendance(state, payload) {
    // Só desliga a edição se o comando pertence à consulta atualmente
    // carregada. Um finishAttendance assíncrono/atrasado de uma consulta
    // anterior (estado global único) não pode sobrescrever o canEdit da
    // consulta seguinte. Sem attendanceId (chamadas legadas) mantém o
    // comportamento antigo.
    const attendanceId = payload?.attendanceId
    if (attendanceId && state.attendance?.id !== attendanceId) return
    state.canEdit = false
  },
  editAttendance(state) {
    state.canEdit = true
    state.attendance.status = 'OPENED'
  },
  setAttendanceEndDatetime(state) {
    // Não atualizar data de edição pré-consulta
    state.attendance.end_datetime =
      state.attendance?.end_datetime ?? Vue.prototype.moment()
  },
  async populateMedicalRecords(state) {
    if (!state.attendance) return
    const attendanceIdSnapshot = state.attendance.id
    state.loading = true
    addAdditionalExamsToForm(state.attendance)
    Vue.prototype.api
      .getPatientSummary(state.attendance?.patient.id, summaryTypes)
      .then(({ data }) => {
        if (state.attendance?.id !== attendanceIdSnapshot) return
        const form = populateMedicalRecords(
          state.canEdit,
          state.attendance,
          state.form,
          data
        )
        state.form = form
      })
      .catch(err => {
        console.error('PopulateMedicalRecords error ', err)
      })
      .finally(() => {
        state.loading = false
      })
  },
  setDocs(state, payload) {
    state.docs = payload
  },
  setExams(state, payload) {
    state.exams = payload
  },
  async getTreatmentRequests(state) {
    if (!state.attendance?.patient?.id) return
    const attendanceIdSnapshot = state.attendance.id

    Vue.prototype.api
      .findLastMedicalRecord(
        'solicitacao-de-tratamento',
        state.attendance?.patient?.id
      )
      .then(({ data }) => {
        if (state.attendance?.id !== attendanceIdSnapshot) return
        if (!state.canEdit) return
        if (state.attendance.type !== 'ATTENDANCE') return
        if (!data?.value) return

        const activeTreatments = data.value.filter(
          item =>
            item.active &&
            item.sessoes.some(sessao => sessao.situacao === 'Pendente')
        )

        if (activeTreatments.length > 0) {
          this.dispatch(`attendanceStore/updateFormValue`, {
            id: 'solicitacao-de-tratamento',
            value: activeTreatments
          })

          this.dispatch(`attendanceStore/updateFormValue`, {
            id: 'solicitacao-de-tratamento',
            value: activeTreatments
          })

          this.dispatch(`attendanceStore/updateMedicalRecord`, {
            id: 'solicitacao-de-tratamento',
            fields: null,
            value: activeTreatments,
            multiple: true
          })

          const modules = Object.values(state.attendanceFormModules[0])
          const category = modules.find(el =>
            el.modules.some(
              module => module.module_name_id === 'solicitacao-de-tratamento'
            )
          )
          const module = category.modules.find(
            el => el.module_name_id === 'solicitacao-de-tratamento'
          )

          category.showing = true
          category.opened = true
          module.showing = true
          module.opened = true

          this.dispatch(
            `attendanceStore/changeVisibilityCategoryModule`,
            { module, category, status: true },
            { root: true }
          )
        }
      })
      .catch(err => {
        console.error('GetTreatmentRequests error ', err)
      })
  },
  handleProps: (state, { key, value }) => {
    state[key] = value
  },
  setAttendanceSelectForm(state, payload) {
    state.selected = { forms: payload }
  },
  setAttendanceSelectFormLabel(state, payload) {
    state.selected.label = payload
  },
  mapForms(state, payload) {
    Object.keys(state).map(key => {
      if (key.includes('exame-personalizado')) {
        removeAdditionalExamModule(key)
      }
    })

    if (!payload || !payload.forms) {
      Object.keys(state).map(key => {
        if (state[key] && typeof state[key] === 'object' && state[key].id) {
          state[key] = {
            ...state[key],
            showing: ![
              'dadosDaCirurgia',
              'anestesia',
              'produtosUtilizados',
              'patologiaASerTratada',
              'anotacaoCirurgica',
              'intercorrencias'
            ].includes(key),
            opened: true
          }
        }
      })
      return
    }

    const formsMap = payload.forms.reduce((acc, form) => {
      if (form.id?.includes('exame-personalizado')) {
        addAdditionalExamsFromForms(form.id, form.customName ?? form.name)
      }
      acc[form.id] = form
      return acc
    }, {})

    Object.keys(state).map(key => {
      if (
        state[key] &&
        typeof state[key] === 'object' &&
        formsMap[state[key].id]
      ) {
        delete formsMap[state[key].id].fields
        delete formsMap[state[key].id].active
        delete formsMap[state[key].id].name
        state[key] = {
          ...state[key],
          ...formsMap[state[key].id]
        }
      }
    })
  },
  handleActiveModule(state, payload) {
    state.active = payload
  },
  updateForms(state, payload) {
    payload.forEach(form => {
      const index = state.selected.forms.findIndex(el => {
        return el.id === form.id
      })
      if (index >= 0) state.selected.forms[index] = form
      else state.selected.forms.push(form)
    })
  },
  updateFormValue(state, payload) {
    // Callback obsoleto após navegação/finalização: sem consulta carregada
    // não há o que atualizar. Evita o crash "reading 'medicalRecords' of
    // null" evidenciado no replay do Datadog (regressão Vitor).
    if (!state.attendance) return
    Object.values(state.selectedForm[0]).map(category => {
      Object.values(category.modules).map(module => {
        if (module.module_name_id === payload.id) {
          module.value = payload.value
        }
      })
    })

    const indexMedicalRecord = state.attendance.medicalRecords.findIndex(
      record => record.type === payload.id
    )
    const medicalRecords = [...state.attendance.medicalRecords]
    if (indexMedicalRecord >= 0) {
      medicalRecords[indexMedicalRecord].value = payload.value
    } else {
      medicalRecords.push({
        type: payload.id,
        value: payload.value
      })
    }
    state.attendance = {
      ...state.attendance,
      medicalRecords: medicalRecords
    }
  },

  addFormModule(state, { category, id, value }) {
    Object.values(state.attendanceFormModules[0]).map(cat => {
      if (cat.module_name_id === category) {
        cat.modules[category] = { value: value }
      }
    })
  },

  updateAttendanceFormValue(state, { id, value }) {
    Object.values(state.attendanceFormModules[0]).map(category => {
      Object.values(category.modules).map(module => {
        if (module.module_name_id === id) {
          module.value = value
        }
      })
    })
  },

  updateAttendanceFormFromMr(state, attendance) {
    for (const mr of attendance.medicalRecords) {
      Object.values(state.attendanceFormModules[0]).map(category => {
        category.modules.map(module => {
          if (
            mr.type === module.module_name_id &&
            module.scopes.includes('ATTENDANCE') === true
          ) {
            module.showing = true
            module.opened = true
            category.showing = true
            category.opened = true
          }
        })
      })
    }
  },

  updateModuleVisibility(state, module) {
    const form = Object.values(state.attendanceFormModules[0]).find(
      form => form.id === module.module_name_id
    )
    if (form) {
      form.showing = form.showing === module.showing ? false : module.showing
    }
  },

  updateCategoryVisibility(state, category) {
    const form = Object.values(state.attendanceFormModules[0]).find(
      form => form.id === category.id
    )
    if (form) {
      form.showing = category.showing
    }
  },
  setAttendanceFormId(state, payload) {
    state.attendanceData.form_id = payload
  },
  changeVisibilityCategoryModule(state, { module, category, status }) {
    Object.values(state.selectedForm[0]).map(category => {
      if (typeof category === 'object') {
        Object.values(category.modules).map(form => {
          if (form.id === module.module_name_id) {
            form.showing = status
            form.opened = status
          }
        })
      }
    })

    Object.values(state.selectedForm[0]).map(categoryModule => {
      if (categoryModule.id === category.id) {
        categoryModule.modules.map(moduleStored => {
          if (moduleStored.module_name_id === module.module_name_id) {
            moduleStored.showing = status
            moduleStored.opened = status
          }

          return module
        })

        categoryModule.showing = categoryModule.modules.some(
          module => module.showing
        )
        return categoryModule
      }
    })
  },
  showModule(state, moduleId) {},
  addAditionalModule(state, { category, module }) {
    const additionalModule = {
      id: module.id,
      module_name_id: module.id,
      module_name: module.customName,
      showing: module.opened,
      opened: module.opened,
      customName: module.customName,
      label: module.label,
      component_name: module.component_name
    }

    state.selectedForm[0][category.module_name].modules.push(additionalModule)
  },
  setCertificate(state, payload) {
    state.certificate = payload
    // Notifica o shell React (Consulta 3.0) para reagir à mudança de assinatura
    // na MESMA aba — o `storage` event nativo só dispara em outras abas.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('singItemAutentic:changed'))
    }
  },
  setOculosImporting(state, status) {
    state.isOculosImporting = status
  },
  setSavingMedicalRecord(state, status) {
    state.savingMedicalRecord = status
  },
  setSavingMedicalRecordError(state, error) {
    state.savingMedicalRecordError = error
  },
  clearSavingMedicalRecord(state) {
    state.savingMedicalRecord = false
    state.savingMedicalRecordError = null
  },
  markMedicalRecordAsSaved(state, type) {
    if (type) {
      state.savedMedicalRecordTypes.add(type)
    }
  },
  resetSavedMedicalRecordTypes(state) {
    state.savedMedicalRecordTypes = new Set()
  }
}
